import { CheckboxInput, type CheckboxInputProps } from '@frontend/components/basic-forms';
import { ICONS } from '@frontend/icons';
import { useId } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import { DropOffProofOfIdentityHelpMessage, PickUpProofOfIdentityHelpMessage, RequireProofOfIdentityLabel } from '../messages/proof-of-identity.messages';

const TOOLTIP_CLASS_NAME =
    'border-border-default bg-background-surface z-1000 max-w-xs rounded-md border shadow-sm [&_.tooltip-inner]:bg-background-surface [&_.tooltip-inner]:text-foreground-default [&_.tooltip-inner]:max-w-xs [&_.tooltip-inner]:p-3 [&_.tooltip-inner]:text-start';

export type ProofOfIdentityRequirementType = 'drop-off' | 'pick-up';

interface ProofOfIdentityCheckboxProps {
    id: string;
    value: boolean;
    onChange: NonNullable<CheckboxInputProps['onChange']>;
    type: ProofOfIdentityRequirementType;
}

export const ProofOfIdentityCheckbox = ({ id, value, onChange, type }: ProofOfIdentityCheckboxProps) => {
    const intl = useIntl();
    const tooltipId = useId().replace(/:/g, '');
    const helpMessage = type === 'drop-off' ? <DropOffProofOfIdentityHelpMessage /> : <PickUpProofOfIdentityHelpMessage />;

    return (
        <div className='flex items-center gap-2'>
            <CheckboxInput
                id={id}
                value={value}
                onChange={onChange}
                label={<RequireProofOfIdentityLabel />}
            />
            <OverlayTrigger
                trigger={['hover', 'focus', 'click']}
                placement='top'
                delay={{ show: 200, hide: 100 }}
                rootClose
                overlay={
                    <Tooltip
                        id={tooltipId}
                        className={TOOLTIP_CLASS_NAME}>
                        {helpMessage}
                    </Tooltip>
                }>
                <button
                    type='button'
                    aria-label={intl.formatMessage({
                        id: 'platform.wizard.proof_of_identity.help_label',
                        description: 'Accessible label for the proof-of-identity help icon in the platform wizard',
                        defaultMessage: 'Explain proof of identity requirement'
                    })}
                    aria-describedby={tooltipId}
                    className='text-foreground-muted hover:text-brand-primary focus-visible:outline-brand-primary inline-flex size-5 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2'>
                    <ICONS.BUTTON.INFO className='size-4' />
                </button>
            </OverlayTrigger>
        </div>
    );
};

export default ProofOfIdentityCheckbox;
